import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companiesApi } from '../../features/empresa/api';
import type { CompanyListItem } from '../../features/empresa/types';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

export interface EmpresaSelectProps {
  value: number | null;
  onChange: (id: number | null, item: CompanyListItem | null) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  includeInactive?: boolean;
}

const PAGE_SIZE = 50;

export function EmpresaSelect({
  value,
  onChange,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  includeInactive,
}: EmpresaSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const search = useDebouncedValue(input, 250);

  const list = useQuery({
    queryKey: ['companies', 'select', { search, includeInactive }],
    queryFn: () =>
      companiesApi.list({
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: 'nome',
        sortOrder: 'asc',
        search: search || undefined,
        includeInactive: includeInactive ?? false,
      }),
    enabled: open,
    placeholderData: (previous) => previous,
  });

  const detail = useQuery({
    queryKey: ['companies', 'detail', value],
    queryFn: () => companiesApi.get(value as number),
    enabled: value !== null && value !== undefined,
  });

  const options: CompanyListItem[] = list.data?.data ?? [];
  const selectedFromList = options.find((o) => o.id === value) ?? null;
  const selected: CompanyListItem | null =
    selectedFromList ??
    (detail.data
      ? {
          id: detail.data.id,
          nome: detail.data.nome,
          documento: detail.data.documento,
          telefone: detail.data.telefone,
          ativo: detail.data.ativo,
          criadoEm: detail.data.criadoEm,
          criadoPorNome: detail.data.criadoPorNome,
          atualizadoEm: detail.data.atualizadoEm,
        }
      : null);

  return (
    <Autocomplete<CompanyListItem>
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') setInput(v);
      }}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.nome}
      options={options}
      loading={list.isFetching}
      disabled={disabled}
      noOptionsText={t('components.asyncSelect.noOptions')}
      loadingText={t('components.asyncSelect.loading')}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('components.empresaSelect.label')}
          placeholder={placeholder ?? t('components.empresaSelect.placeholder')}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
